import {
  Activity,
  Calendar,
  Edit3,
  MapPin,
  Plus,
  Save,
  Trash2,
  User,
} from "lucide-react";
import React, { useState } from "react";
import { useLocaleFormat } from "../../hooks/useLocaleFormat";
import {
  healthConditionsStrings,
  type HealthConditionsStrings,
  isBuiltInLabel,
} from "../../locales/modules/healthConditions";
import { useI18nStore } from "../../stores/i18nStore";
import { TreatmentHistory } from "../../types/fhir-health";

interface TreatmentHistoryProps {
  treatments: TreatmentHistory[];
  onSave: (treatments: TreatmentHistory[]) => void;
  isEditing: boolean;
  onToggleEdit: () => void;
  isSaving: boolean;
}

// Common treatment types with codes. The code identifies the entry; the name
// is a translation key, and the English label is what gets stored as the
// record's display text. `legacy` lists the German names older versions
// stored, so those records are shown in the active language too.
type TreatmentKey = keyof HealthConditionsStrings;

const COMMON_TREATMENTS: {
  code: string;
  nameKey: TreatmentKey;
  type: TreatmentHistory["type"];
  legacy?: string[];
}[] = [
  // Medical procedures
  {
    code: "33195004",
    nameKey: "treatNameBloodPressureCheck",
    type: "procedure",
  },
  {
    code: "71388002",
    nameKey: "treatNameRoutineCheckup",
    type: "procedure",
    legacy: ["Routine-Untersuchung"],
  },
  { code: "185349003", nameKey: "treatNameVaccination", type: "procedure" },
  { code: "182836005", nameKey: "treatNameConsultation", type: "procedure" },
  { code: "386053000", nameKey: "treatNameBloodTest", type: "procedure" },
  { code: "16310003", nameKey: "treatNameXray", type: "procedure" },
  { code: "363680008", nameKey: "treatNameUltrasound", type: "procedure" },

  // Therapies
  { code: "91251008", nameKey: "treatNamePhysicalTherapy", type: "therapy" },
  { code: "75516001", nameKey: "treatNamePsychotherapy", type: "therapy" },
  {
    code: "76746007",
    nameKey: "treatNameOccupationalTherapy",
    type: "therapy",
  },
  { code: "18512000", nameKey: "treatNameRespiratoryTherapy", type: "therapy" },
  { code: "229060009", nameKey: "treatNameMassage", type: "therapy" },

  // Surgeries
  {
    code: "387713003",
    nameKey: "treatNameMinorSurgery",
    type: "surgery",
    legacy: ["Kleine Operation"],
  },
  { code: "26643006", nameKey: "treatNameArthroscopy", type: "surgery" },
  {
    code: "80146002",
    nameKey: "treatNameAppendectomy",
    type: "surgery",
    legacy: ["Blinddarm-OP"],
  },
  {
    code: "232717009",
    nameKey: "treatNameHeartSurgery",
    type: "surgery",
    legacy: ["Herz-OP"],
  },

  // Common medications (for reference)
  {
    code: "N02BE01",
    nameKey: "treatNameAcetaminophenTherapy",
    type: "medication",
  },
  {
    code: "J01CR02",
    nameKey: "treatNameAntibioticCourse",
    type: "medication",
    legacy: ["Antibiotika-Kur"],
  },
  {
    code: "C09AA02",
    nameKey: "treatNameBloodPressureMedication",
    type: "medication",
  },
];

const TYPE_KEYS: Record<string, TreatmentKey> = {
  procedure: "treatTypeProcedure",
  therapy: "treatTypeTherapy",
  surgery: "treatTypeSurgery",
  medication: "treatTypeMedication",
};

const STATUS_KEYS: Record<string, TreatmentKey> = {
  completed: "treatStatusCompleted",
  "in-progress": "treatStatusInProgress",
  planned: "treatStatusPlanned",
  cancelled: "treatStatusCancelled",
};

export const TreatmentHistoryComponent: React.FC<TreatmentHistoryProps> = ({
  treatments,
  onSave,
  isEditing,
  onToggleEdit,
  isSaving,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const label = (map: Record<string, TreatmentKey>, code?: string) => {
    const key = code ? map[code] : undefined;
    return key ? t[key] : (code ?? "");
  };

  // Built-in treatments are shown in the active language, also for records
  // that older versions stored with a German name; free text stays as typed.
  const treatmentName = (treatment: TreatmentHistory) => {
    const entry = COMMON_TREATMENTS.find((c) => c.code === treatment.code);
    if (
      entry &&
      isBuiltInLabel(entry.nameKey, treatment.display, entry.legacy)
    ) {
      return t[entry.nameKey];
    }
    return treatment.display;
  };
  const [localTreatments, setLocalTreatments] =
    useState<TreatmentHistory[]>(treatments);

  const addNewTreatment = () => {
    const newTreatment: TreatmentHistory = {
      id: crypto.randomUUID(),
      type: "procedure",
      code: "",
      display: "",
      status: "completed",
      performedDate: new Date().toISOString().split("T")[0] || "",
      performer: "",
    };
    setLocalTreatments([...localTreatments, newTreatment]);
  };

  const updateTreatment = (
    index: number,
    field: keyof TreatmentHistory,
    value: string | boolean | Date,
  ) => {
    const updated = [...localTreatments];
    const current = updated[index];

    if (field === "code") {
      // Auto-fill treatment name and type
      const treatment = COMMON_TREATMENTS.find((c) => c.code === value);
      if (treatment && current) {
        updated[index] = {
          ...current,
          code: value,
          display: healthConditionsStrings.en[treatment.nameKey],
          type: treatment.type,
        } as TreatmentHistory;
      } else if (current) {
        updated[index] = { ...current, [field]: value } as TreatmentHistory;
      }
    } else if (current) {
      updated[index] = { ...current, [field]: value } as TreatmentHistory;
    }
    setLocalTreatments(updated);
  };

  const removeTreatment = (index: number) => {
    const updated = localTreatments.filter((_, i) => i !== index);
    setLocalTreatments(updated);
  };

  const handleSave = () => {
    onSave(localTreatments);
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case "medication":
        return "text-primary-600 bg-primary-50 border-primary-200";
      case "procedure":
        return "text-green-600 bg-green-50 border-green-200";
      case "therapy":
        return "text-purple-600 bg-purple-50 border-purple-200";
      case "surgery":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case "completed":
        return "text-green-700";
      case "in-progress":
        return "text-primary-700";
      case "planned":
        return "text-yellow-700";
      case "cancelled":
        return "text-red-700";
      default:
        return "text-gray-700";
    }
  };

  const getTypeIcon = (type?: string) => {
    switch (type) {
      case "medication":
        return "💊";
      case "procedure":
        return "🔬";
      case "therapy":
        return "🧘";
      case "surgery":
        return "🏥";
      default:
        return "📋";
    }
  };

  // Sort treatments by date (newest first)
  const sortedTreatments = [...treatments].sort(
    (a, b) =>
      new Date(b.performedDate).getTime() - new Date(a.performedDate).getTime(),
  );

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
          <Activity className="w-5 h-5 text-green-600" />
          <span>{t.treatmentHistory || "Treatment History"}</span>
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={onToggleEdit}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title={isEditing ? t.cancel : t.edit}
            aria-label={isEditing ? t.cancel : t.edit}
          >
            <Edit3 className="w-4 h-4" />
          </button>
          {isEditing && (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 transition-colors flex items-center space-x-1"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
              ) : (
                <Save className="w-3 h-3" />
              )}
              <span>{isSaving ? t.saving : t.save}</span>
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-6">
          {localTreatments.map((treatment, index) => (
            <div
              key={treatment.id || index}
              className="border border-gray-200 rounded-lg p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor={`treatment-${index}-type`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldType}
                  </label>
                  <select
                    id={`treatment-${index}-type`}
                    value={treatment.type}
                    onChange={(e) =>
                      updateTreatment(index, "type", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(TYPE_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>
                        {t[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    htmlFor={`treatment-${index}-status`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.status}
                  </label>
                  <select
                    id={`treatment-${index}-status`}
                    value={treatment.status}
                    onChange={(e) =>
                      updateTreatment(index, "status", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {Object.entries(STATUS_KEYS).map(([value, key]) => (
                      <option key={value} value={value}>
                        {t[key]}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`treatment-${index}-code`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldTreatment}
                  </label>
                  <select
                    id={`treatment-${index}-code`}
                    value={treatment.code}
                    onChange={(e) =>
                      updateTreatment(index, "code", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">{t.pleaseSelect}</option>
                    {COMMON_TREATMENTS.filter(
                      (c) => c.type === treatment.type,
                    ).map((treat) => (
                      <option key={treat.code} value={treat.code}>
                        {t[treat.nameKey]}
                      </option>
                    ))}
                  </select>
                  {treatment.code &&
                    !COMMON_TREATMENTS.find(
                      (c) => c.code === treatment.code,
                    ) && (
                      <input
                        type="text"
                        value={treatment.display}
                        onChange={(e) =>
                          updateTreatment(index, "display", e.target.value)
                        }
                        className="w-full px-3 py-2 mt-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder={t.treatCustomPlaceholder}
                      />
                    )}
                </div>

                <div>
                  <label
                    htmlFor={`treatment-${index}-performed-date`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldDate}
                  </label>
                  <input
                    id={`treatment-${index}-performed-date`}
                    type="date"
                    value={treatment.performedDate.split("T")[0]}
                    onChange={(e) =>
                      updateTreatment(index, "performedDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label
                    htmlFor={`treatment-${index}-performer`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldPerformer}
                  </label>
                  <input
                    id={`treatment-${index}-performer`}
                    type="text"
                    value={treatment.performer}
                    onChange={(e) =>
                      updateTreatment(index, "performer", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.treatPerformerPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`treatment-${index}-location`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldLocation}
                  </label>
                  <input
                    id={`treatment-${index}-location`}
                    type="text"
                    value={treatment.location || ""}
                    onChange={(e) =>
                      updateTreatment(index, "location", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={t.treatLocationPlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`treatment-${index}-outcome`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldOutcome}
                  </label>
                  <textarea
                    id={`treatment-${index}-outcome`}
                    value={treatment.outcome || ""}
                    onChange={(e) =>
                      updateTreatment(index, "outcome", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={2}
                    placeholder={t.treatOutcomePlaceholder}
                  />
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor={`treatment-${index}-notes`}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.treatFieldNotes}
                  </label>
                  <textarea
                    id={`treatment-${index}-notes`}
                    value={treatment.notes || ""}
                    onChange={(e) =>
                      updateTreatment(index, "notes", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    rows={2}
                    placeholder={t.treatNotesPlaceholder}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => removeTreatment(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title={t.treatRemove}
                  aria-label={t.treatRemove}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          <button
            onClick={addNewTreatment}
            className="w-full px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t.treatAdd}</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedTreatments.length === 0 ? (
            <p className="text-gray-500 italic">
              {t.noTreatmentHistoryDocumented}
            </p>
          ) : (
            sortedTreatments.map((treatment, index) => {
              const typeColor = getTypeColor(treatment.type);
              const statusColor = getStatusColor(treatment.status);
              const typeIcon = getTypeIcon(treatment.type);

              return (
                <div
                  key={treatment.id || index}
                  className={`border rounded-lg p-4 ${typeColor}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900 flex items-center space-x-2">
                          <span className="text-lg">{typeIcon}</span>
                          <span>{treatmentName(treatment)}</span>
                        </h4>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`text-xs px-2 py-1 rounded bg-white bg-opacity-50 ${statusColor}`}
                          >
                            {label(STATUS_KEYS, treatment.status)}
                          </span>
                          <div className="text-xs px-2 py-1 rounded bg-white bg-opacity-50">
                            {label(TYPE_KEYS, treatment.type)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 text-sm text-gray-700">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>{fmt.date(treatment.performedDate)}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <User className="w-3 h-3" />
                            <span>{treatment.performer}</span>
                          </span>
                          {treatment.location && (
                            <span className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3" />
                              <span>{treatment.location}</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {treatment.outcome && (
                        <div className="mt-2 text-sm text-gray-700">
                          <strong>{t.treatOutcomeLabel}</strong>{" "}
                          {treatment.outcome}
                        </div>
                      )}

                      {treatment.notes && (
                        <div className="mt-1 text-xs text-gray-600 italic">
                          {treatment.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
